import { PaymentPlans, SectionIds, Stages } from "./consts";
import type {
  PaymentPlan,
  PriceFacts,
  PriceRowSpec,
  PriceView,
  SectionId,
} from "./types";
import { PRICE_ROWS } from "./ui";

const GUEST_PAYMENT_SCHEDULE_PLANS: readonly PaymentPlan[] = [
  PaymentPlans.PayAtVenue,
  PaymentPlans.SplitPayment,
  PaymentPlans.ZeroDown,
];

const hasGuestPaymentSchedule = (view: PriceView): boolean =>
  GUEST_PAYMENT_SCHEDULE_PLANS.includes(view.paymentPlan);

/**
 * - Guest: always Section 1; Section 3 when a payment schedule is in play
 *   (split / zero-down / pay-at-venue), or after checkout so a pay-in-full
 *   price change can show Already paid / Difference due today. Empty sections
 *   are dropped, so PostCheckout pay-in-full with no price difference stays
 *   hidden. Suppressed for cancelled / expired / inquired PAV.
 * - Host: Sections 1 + 2; Section 3 unless suppressed (inquired / terminal PAV)
 * - Inquiry outside-base: no standard sections (message on model)
 * - Collected at venue: retired with UK FSV inbox layouts
 *  Figma link that explains section visibility:
 *  https://www.figma.com/design/DZnoqMO7gjqmByX5uTEhyE/Unified-pricing-%E2%80%94-Q3.26?node-id=0-1&p=f&m=dev
 */
const isSectionVisible = (section: SectionId, view: PriceView): boolean => {
  if (view.inquiryOutsideBase != null) {
    return false;
  }

  switch (section) {
    case SectionIds.PriceBreakdown:
      return true;
    case SectionIds.HostEarnings:
      return view.perspective === "host";
    case SectionIds.PaymentPayout:
      if (view.perspective === "host") {
        return view.suppressHostPayoutHighlight !== true;
      }
      return (
        (hasGuestPaymentSchedule(view) ||
          view.stage === Stages.PostCheckout) &&
        view.suppressGuestPaymentSchedule !== true
      );
    case SectionIds.CollectedAtVenue:
      return false;
    default:
      return false;
  }
};

/** Whether the row renders: section, perspective, amount (incl. zero), then `show`. */
const isVisible = (
  spec: PriceRowSpec,
  facts: PriceFacts,
  view: PriceView,
): boolean => {
  if (!isSectionVisible(spec.section, view)) return false;
  if (spec.perspective != null && spec.perspective !== view.perspective) {
    return false;
  }

  const amount = spec.amount(facts, view);
  if (amount === null || amount === undefined) {
    return false;
  }

  const zeroIsMeaningful = spec.allowZero === true || spec.zeroAs !== undefined;
  if (amount === 0 && !zeroIsMeaningful) {
    return false;
  }

  if (spec.show === undefined) return true;
  return typeof spec.show === "function" ? spec.show(facts, view) : spec.show;
};

/** Specs from `PRICE_ROWS` that pass `isVisible` for this facts/view. */
const getVisibleSpecs = ({
  facts,
  view,
}: {
  facts: PriceFacts;
  view: PriceView;
}): PriceRowSpec[] => PRICE_ROWS.filter((spec) => isVisible(spec, facts, view));

export { getVisibleSpecs, isSectionVisible };
